import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { CheckCircle, ArrowLeft, Phone, Mail, Calendar, DollarSign } from "lucide-react";
import WeatherCard from "@/components/WeatherCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function BookingConfirmation() {
  const [latestReservation, setLatestReservation] = useState(null);
  const [product, setProduct] = useState(null);

  useEffect(() => {
    const fetchLatestReservation = async () => {
      try {
        const reservations = await base44.entities.Reservation.list("-created_date", 1);
        if (reservations && reservations.length > 0) {
          const reservation = reservations[0];
          setLatestReservation(reservation);
          
          if (reservation.product_id) {
            const productData = await base44.entities.Product.get(reservation.product_id);
            setProduct(productData);
          }
        }
      } catch (error) {
        console.error("Error fetching latest reservation:", error);
      }
    };

    fetchLatestReservation();
  }, []);

  if (!latestReservation) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Loading...</h1>
          <Link to="/WaterSlides">
            <Button>Browse Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  const rentalPrice = product?.price || latestReservation.total_price;
  const reservationFee = 40;
  const balanceDue = latestReservation.total_price - reservationFee;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-2">
            Your Reservation is Confirmed!
          </h1>
          <p className="text-lg text-muted-foreground">
            Reservation #{latestReservation.reservation_number}
          </p>
        </motion.div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Reservation Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Product</p>
                <p className="font-semibold">{product?.name || "Water Slide"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Date</p>
                <p className="font-semibold flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(latestReservation.event_date).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge className="bg-green-100 text-green-800">Confirmed</Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Payment Status</p>
                <Badge className="bg-green-100 text-green-800">Reservation Fee Paid</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Pricing Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Rental Price</span>
                <span className="font-medium">${rentalPrice}</span>
              </div>
              <div className="border-t pt-2 flex justify-between font-bold text-lg">
                <span>Total</span>
                <span>${latestReservation.total_price}</span>
              </div>
              <div className="bg-green-50 p-3 rounded-lg flex justify-between items-center border border-green-200">
                <span className="text-green-700 font-medium">✓ $40 Reservation Fee Received</span>
                <span className="font-bold text-green-700">-$40</span>
              </div>
              <div className="border-t pt-2 flex justify-between font-bold text-lg">
                <span>Remaining Balance</span>
                <span className="text-primary">${balanceDue}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {latestReservation.weather_checked && (
          <div className="mb-6">
            <WeatherCard
              weather={{
                status: latestReservation.weather_status,
                weather_high_temp: latestReservation.weather_high_temp,
                weather_rain_chance: latestReservation.weather_rain_chance,
                weather_message: latestReservation.weather_message,
                weather_forecast_date: latestReservation.weather_forecast_date,
              }}
              eventDate={latestReservation.weather_forecast_date || latestReservation.event_date}
            />
          </div>
        )}

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <h3 className="font-bold mb-3 text-lg">What Happens Next?</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-blue-700">1</span>
                </div>
                <p className="text-sm">Your $40 reservation fee has been received and your date is now secured.</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-blue-700">2</span>
                </div>
                <p className="text-sm">You'll receive a confirmation email with all the details shortly.</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-blue-700">3</span>
                </div>
                <p className="text-sm">Your remaining balance is due before delivery — we'll contact you to arrange payment.</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-blue-700">4</span>
                </div>
                <p className="text-sm">On the event day, we'll deliver and set up — then you enjoy!</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Need Help?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a href="tel:8329704583" className="flex items-center gap-3 p-4 border rounded-lg hover:bg-muted transition-colors">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Call Us</p>
                  <p className="text-sm text-muted-foreground">832-970-4583</p>
                </div>
              </a>
              <a href="mailto:info@waterslideshouston.com" className="flex items-center gap-3 p-4 border rounded-lg hover:bg-muted transition-colors">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Email Us</p>
                  <p className="text-sm text-muted-foreground">info@waterslideshouston.com</p>
                </div>
              </a>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <Link to="/WaterSlides">
            <Button variant="outline" className="mr-2">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Browse More Products
            </Button>
          </Link>
          <a href="tel:8329704583">
            <Button>
              <Phone className="w-4 h-4 mr-2" />
              Call Us: 832-970-4583
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}